import { useEffect, useState } from 'react';
import { readPref, writePref } from './prefs';

// Light or dark mode. 'system' follows the device; 'light' and 'dark' set data-theme on <html>,
// which the stylesheet and every color scheme already honour. Stored in this browser for the first
// paint, and with a signed-in user also in their account (schemeSync.ts).

export type Theme = 'light' | 'dark' | 'system';
export const THEME_CHANGED = 'mybooks:theme-changed';
const THEMES: Theme[] = ['light', 'dark', 'system'];

export function readTheme(): Theme {
  const v = readPref('mybooks.theme');
  return THEMES.includes(v as Theme) ? (v as Theme) : 'system';
}

const deviceDark = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;

/** What is on screen: the chosen mode, or the device's when following it. */
export const effectiveTheme = (t: Theme = readTheme()): 'light' | 'dark' => (t === 'system' ? (deviceDark() ? 'dark' : 'light') : t);

export function applyTheme(t: Theme) {
  writePref('mybooks.theme', t);
  if (t === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
  window.dispatchEvent(new Event(THEME_CHANGED));
}

/** Called once at startup, before the first render. */
export const installTheme = () => applyTheme(readTheme());

/** The chosen mode and what is on screen, kept current when either changes. */
export function useTheme(): { theme: Theme; effective: 'light' | 'dark' } {
  const [state, setState] = useState(() => ({ theme: readTheme(), effective: effectiveTheme() }));
  useEffect(() => {
    const update = () => setState({ theme: readTheme(), effective: effectiveTheme() });
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    window.addEventListener(THEME_CHANGED, update);
    media?.addEventListener('change', update);
    return () => { window.removeEventListener(THEME_CHANGED, update); media?.removeEventListener('change', update); };
  }, []);
  return state;
}
